import { MIN_PROGRESSIVE_DEBOUNCE_MS } from "../timing";

export interface ProgressiveDebounceOptions {
  initialMs: number;
  multiplier?: number;
  windowSize?: number;
}

export class ProgressiveDebounce {
  private _times: number[];
  private _timer: ReturnType<typeof setTimeout> | null = null;
  private readonly _multiplier: number;
  private readonly _windowSize: number;

  constructor(initialMs: number, multiplier?: number, windowSize?: number);
  constructor(options: ProgressiveDebounceOptions);
  constructor(initialMsOrOptions: number | ProgressiveDebounceOptions, multiplier: number = 2, windowSize: number = 5) {
    if (typeof initialMsOrOptions === "object") {
      const { initialMs, multiplier: mult = 2, windowSize: ws = 5 } = initialMsOrOptions;
      this._multiplier = mult;
      this._windowSize = ws;
      this._times = Array(this._windowSize).fill(initialMs);
    } else {
      this._multiplier = multiplier;
      this._windowSize = windowSize;
      this._times = Array(this._windowSize).fill(initialMsOrOptions);
    }
  }

  getCurrentDebounce(): number {
    const avg = this._times.reduce((a, b) => a + b, 0) / this._times.length;
    const debounce = avg * this._multiplier;
    return Math.max(MIN_PROGRESSIVE_DEBOUNCE_MS, debounce);
  }

  schedule(callback: () => void): () => void {
    this.clear();
    this._timer = setTimeout(callback, this.getCurrentDebounce());
    return () => this.clear();
  }

  clear(): void {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  recordExecution(elapsedMs: number): void {
    this._times.push(elapsedMs);
    this._times.shift();
  }
}
